/**
 * bb-addons.js
 * ───────────────────────────────────────────────────────────────────────────
 * Planting & Guarantee add-on upsell (feature spec: docs/addon-services-feature-spec.md).
 *
 * When a `guarantee`-tagged plant is added to cart AND the selected variant's SKU size token
 * matches at least one add-on in `green-glove-planting-services` / `guarantees`, we intercept
 * the add, open a modal of matched services, and write the parent + chosen add-ons to the cart
 * in ONE atomic /cart/add.js call — linked by a shared `_addon_group` line-item property so the
 * cart can nest the add-ons beneath their parent (§9). Products that aren't tagged, or have no
 * size match, are never touched: the theme's own add-to-cart runs unchanged.
 *
 * Interception strategy: the theme delegates `on:submit` / `on:click` via a *document*-level
 * capture listener (assets/component.js). A capture listener on `window` sits one step further
 * out in the propagation path, so it runs FIRST — `stopImmediatePropagation()` there preempts
 * the theme handler cleanly, no registration-order race. We only preempt for eligible+matched
 * forms; otherwise we return without touching the event.
 *
 * Decisions locked from the spec's decision log: D1 add-on-attach (dismiss still adds the
 * plant), D2/D3 at-most-one per category, D4 new group per add action, D5 add-on qty = parent
 * qty (1:1). Pricing is never sent from the client — only {id, quantity, properties} (§10).
 */

import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

const DIALOG_ID = 'bb-addons-dialog';
const DATA_SELECTOR = 'script[data-bb-addons]';
const ADD_BUTTON_SELECTOR = 'button[name="add"], [ref="addToCartButton"]';

/** @typedef {{id: string|number, t: string, title: string, variant: string, price: number, priceText: string, available: boolean, url: string, image: string}} AddOn */

/** Modal/add state for the in-flight add-to-cart. @type {null | {form: HTMLFormElement, component: Element, parentTitle: string, finalized: boolean}} */
let pending = null;
/** Guards re-entrancy while an add is in flight. */
let busy = false;
/** Guards the orphan-reconcile pass from reacting to its own cart update. */
let reconciling = false;

// ── Data + matching ────────────────────────────────────────────────────────

/** @param {Element | null} component @returns {null | {parentTitle: string, variants: Record<string,string>, planting: AddOn[], guarantee: AddOn[]}} */
function readData(component) {
  const el = component?.querySelector(DATA_SELECTOR);
  if (!el?.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.error('[bb-addons] could not parse add-on data', error);
    return null;
  }
}

/** Read the variant id the form will submit. @param {HTMLFormElement} form */
function variantIdOf(form) {
  return String(new FormData(form).get('id') || '');
}

/**
 * Read the quantity to add (defaults to 1). The PDP quantity selector lives in
 * <product-form-component> but OUTSIDE the buy-buttons <form>, so FormData(form)
 * never sees it — read it the way the theme does (product-form.js #getQuantity):
 * the quantity-selector input within the component. Falls back to the form field
 * (cart/quick contexts) then 1.
 * @param {HTMLFormElement} form
 */
function quantityOf(form) {
  const component = form.closest('product-form-component');
  const input = component?.querySelector(
    'quantity-selector-component input[ref="quantityInput"], input[name="quantity"]'
  );
  const raw = input instanceof HTMLInputElement ? input.value : new FormData(form).get('quantity');
  const q = parseInt(String(raw || ''), 10);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

/**
 * Matched add-ons for the selected variant. Tokens are compared as exact, lowercased strings
 * (so `07` ≠ `7`, per §6.2); a blank token (no dash / trailing dash / no SKU) matches nothing.
 * @param {{variants: Record<string,string>, planting: AddOn[], guarantee: AddOn[]}} data
 * @param {string} variantId
 */
function matchesFor(data, variantId) {
  const token = (data.variants && data.variants[variantId]) || '';
  if (!token) return { token: '', planting: [], guarantee: [] };
  const filter = (/** @type {AddOn[]} */ list) => (list || []).filter((a) => a.t === token);
  return { token, planting: filter(data.planting), guarantee: filter(data.guarantee) };
}

// ── Interception ───────────────────────────────────────────────────────────

/** @param {Event} event */
function onClickCapture(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest(ADD_BUTTON_SELECTOR);
  if (!button || button.matches('[disabled]')) return;
  const form = button.closest('form');
  if (form) intercept(event, form);
}

/** Covers keyboard submit (Enter) where no add-button click fires. @param {Event} event */
function onSubmitCapture(event) {
  const form = event.target;
  if (form instanceof HTMLFormElement) intercept(event, form);
}

/**
 * Decide whether to take over this add-to-cart. We only preempt the theme for an eligible
 * (`guarantee`-tagged) product whose selected variant has at least one size match. Everything
 * else returns untouched so Horizon's native flow runs.
 * @param {Event} event
 * @param {HTMLFormElement} form
 */
function intercept(event, form) {
  const component = form.closest('product-form-component');
  if (!component) return;
  const data = readData(component);
  if (!data) return; // not eligible — no add-on data was rendered for this product

  if (busy) {
    // An add is already in flight; swallow the duplicate trigger.
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const match = matchesFor(data, variantIdOf(form));
  if (!match.planting.length && !match.guarantee.length) return; // no size match → native add

  event.preventDefault();
  event.stopImmediatePropagation();
  openModal(form, component, data, match);
}

// ── Modal ──────────────────────────────────────────────────────────────────

/**
 * @param {HTMLFormElement} form
 * @param {Element} component
 * @param {{parentTitle: string}} data
 * @param {{planting: AddOn[], guarantee: AddOn[]}} match
 */
function openModal(form, component, data, match) {
  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(DIALOG_ID));
  const quantity = quantityOf(form);
  pending = { form, component, parentTitle: data.parentTitle || '', quantity, finalized: false };

  // Defensive: if the modal markup is missing, honor the click by adding the plant alone.
  if (!dialog || typeof dialog.showModal !== 'function') {
    finalize([]);
    return;
  }

  renderSection(dialog, 'planting', match.planting, quantity);
  renderSection(dialog, 'guarantee', match.guarantee, quantity);
  updateTotal(dialog);
  dialog.showModal();
  document.documentElement.classList.add('bb-modal-open'); // body scroll-lock [R-10]
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {'planting'|'guarantee'} role
 * @param {AddOn[]} items
 * @param {number} quantity - parent quantity; each add-on is added 1:1 (D5)
 */
function renderSection(dialog, role, items, quantity) {
  const section = dialog.querySelector(`[data-bb-addons-section="${role}"]`);
  const list = dialog.querySelector(`[data-bb-addons-list="${role}"]`);
  const template = /** @type {HTMLTemplateElement | null} */ (dialog.querySelector('#bb-addon-card-template'));
  if (!(section instanceof HTMLElement) || !list || !template) return;

  list.textContent = '';
  if (!items.length) {
    section.hidden = true;
    return;
  }
  section.hidden = false;

  // Opt-out default: preselect the first available add-on in this section, so both
  // categories (planting + guarantee) start selected and the customer clicks to
  // deselect. Single-select per section (D3), so at most one is preselected here.
  let preselected = false;
  for (const item of items) {
    const node = /** @type {HTMLElement} */ (template.content.firstElementChild.cloneNode(true));
    const card = /** @type {HTMLButtonElement} */ (node.querySelector('[data-bb-addon-card]'));
    card.dataset.id = String(item.id);
    card.dataset.role = role;
    card.dataset.price = String(item.price || 0); // numeric (cents) for the live total
    card.setAttribute('aria-pressed', 'false');

    const img = node.querySelector('[data-bb-card-image]');
    if (img instanceof HTMLImageElement) {
      if (item.image) {
        img.src = item.image;
        img.alt = item.title || '';
      } else {
        img.closest('.bb-addon-card__media')?.remove();
      }
    }
    setText(node, '[data-bb-card-title]', item.title || '');
    setText(node, '[data-bb-card-variant]', item.variant && item.variant !== 'Default Title' ? item.variant : '');
    // Reflect the parent quantity in the price (unit × qty) so a multi-qty order is clear.
    let priceText = '';
    if (item.available === false) {
      priceText = 'Sold out';
    } else if (item.priceText) {
      priceText = quantity > 1 ? `${item.priceText} × ${quantity}` : item.priceText;
    }
    setText(node, '[data-bb-card-price]', priceText);

    // Out-of-stock add-on: shown but not selectable (§8 — show disabled with a label).
    if (item.available === false) {
      card.disabled = true;
      card.classList.add('bb-addon-card--unavailable');
    } else if (!preselected) {
      // Default-on: first available option in this section starts selected.
      card.setAttribute('aria-pressed', 'true');
      preselected = true;
    }
    list.appendChild(node);
  }
}

/** @param {HTMLDialogElement} dialog */
function onDialogClick(dialog, event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const card = target.closest('[data-bb-addon-card]');
  if (card instanceof HTMLButtonElement) {
    if (card.disabled) return;
    toggleCard(dialog, card);
    updateTotal(dialog);
    return;
  }
  if (target.closest('[data-bb-addons-confirm]')) {
    finalizeFromDialog(dialog, collectSelected(dialog));
    return;
  }
  if (target.closest('[data-bb-addons-skip]') || target.closest('[data-bb-addons-close]')) {
    finalizeFromDialog(dialog, []);
  }
}

/** Single-select per category (D3): selecting one card clears its siblings. */
function toggleCard(dialog, card) {
  const wasPressed = card.getAttribute('aria-pressed') === 'true';
  for (const sibling of dialog.querySelectorAll(`[data-bb-addon-card][data-role="${card.dataset.role}"]`)) {
    sibling.setAttribute('aria-pressed', 'false');
  }
  card.setAttribute('aria-pressed', wasPressed ? 'false' : 'true');
}

/** @returns {{id: string, role: string}[]} */
function collectSelected(dialog) {
  return [...dialog.querySelectorAll('[data-bb-addon-card][aria-pressed="true"]')].map((card) => ({
    id: /** @type {HTMLElement} */ (card).dataset.id || '',
    role: /** @type {HTMLElement} */ (card).dataset.role || '',
  }));
}

function finalizeFromDialog(dialog, selected) {
  if (pending) pending.finalized = true;
  dialog.close();
  finalize(selected);
}

// ── Writing to cart ──────────────────────────────────────────────────────────

/** Per add action, unique enough for grouping (D4). */
function makeGroupId() {
  return `bb-${Date.now().toString(36)}-${Math.floor(performance.now()).toString(36)}`;
}

/**
 * Build the atomic add payload and write it. Add-ons inherit the parent quantity 1:1 (D5).
 * No price is ever sent — Shopify prices each line from its own variant (§10).
 * @param {{id: string, role: string}[]} selected
 */
async function finalize(selected) {
  if (!pending || busy) return;
  busy = true;

  const { form, component, parentTitle } = pending;
  const parentId = variantIdOf(form);
  const quantity = quantityOf(form);

  /** @type {Array<{id: string, quantity: number, properties?: Record<string,string>}>} */
  const items = [];
  if (selected.length) {
    const groupId = makeGroupId();
    items.push({ id: parentId, quantity, properties: { _addon_group: groupId, _addon_role: 'parent' } });
    for (const addon of selected) {
      items.push({
        id: addon.id,
        quantity,
        properties: { _addon_group: groupId, _addon_role: addon.role, _addon_parent: parentTitle },
      });
    }
  } else {
    // Dismissed / nothing chosen — just the plant, no group needed (D1).
    items.push({ id: parentId, quantity });
  }

  try {
    await addItems(items, component);
  } catch (error) {
    console.error('[bb-addons] add to cart failed', error);
  } finally {
    busy = false;
    pending = null;
  }
}

/**
 * POST the bundle and refresh the cart UI the same way the native form does: request the
 * cart sections and dispatch a CartAddEvent so the bubble + drawer re-render.
 * @param {Array<{id: string, quantity: number, properties?: Record<string,string>}>} items
 * @param {Element} component
 */
async function addItems(items, component) {
  // Global loading state on the add button while the bundle POSTs (bb-design.css
  // styles [aria-busy="true"]). Cleared in finally for both success and error. [loading]
  const addButton = component?.querySelector(ADD_BUTTON_SELECTOR);
  addButton?.setAttribute('aria-busy', 'true');

  try {
    const body = /** @type {Record<string, unknown>} */ ({ items });
    const sections = cartSectionIds();
    if (sections) body.sections = sections;

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (result.status) {
      console.error('[bb-addons] cart rejected the add:', result.message);
      return;
    }

    const cart = await fetchCart();
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    document.dispatchEvent(
      new CartAddEvent(cart ?? undefined, 'bb-addons', {
        source: 'product-form-component',
        itemCount,
        productId: /** @type {HTMLElement} */ (component).dataset.productId,
        sections: result.sections,
      })
    );
  } finally {
    addButton?.removeAttribute('aria-busy');
  }
}

// ── Cart grouping reconciliation (AC8) ───────────────────────────────────────

/**
 * Reconcile the cart after any change. Two passes, applied in one /cart/update.js call:
 *   1. Orphan removal (§9.4 / AC8): any add-on whose group has no surviving `parent` line
 *      is removed (e.g. the parent was deleted).
 *   2. Quantity sync (D5): any add-on whose quantity drifted from its parent's is snapped
 *      back, so editing a tree/plant's quantity on the cart page moves its add-ons with it.
 * @param {CartUpdateEvent} event
 */
async function onCartUpdate(event) {
  if (reconciling) return;
  if (event.detail?.data?.source === 'bb-addons-reconcile') return;

  let cart = event.detail?.resource;
  if (!cart || !Array.isArray(cart.items)) cart = await fetchCart();
  if (!cart) return;

  const orphanKeys = findOrphanKeys(cart);
  const qtyUpdates = findQuantityUpdates(cart, orphanKeys);
  if (!orphanKeys.length && !qtyUpdates.length) return;

  reconciling = true;
  try {
    // One request for both passes: orphans → 0, add-ons → their parent's quantity.
    const updates = /** @type {Record<string, number>} */ (Object.fromEntries(orphanKeys.map((k) => [k, 0])));
    for (const [key, qty] of qtyUpdates) updates[key] = qty;
    const body = /** @type {Record<string, unknown>} */ ({ updates });
    const sections = cartSectionIds();
    if (sections) body.sections = sections;

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const updated = await response.json();
    document.dispatchEvent(
      new CartUpdateEvent(updated, 'bb-addons', {
        source: 'bb-addons-reconcile',
        itemCount: updated.item_count,
        sections: updated.sections,
      })
    );
  } catch (error) {
    console.error('[bb-addons] reconcile failed', error);
  } finally {
    reconciling = false;
  }
}

/**
 * Removing a parent on the cart page must take its add-ons with it INSTANTLY. The theme removes
 * one line per request, so letting it remove the parent and then reacting via reconcile means two
 * sequential round-trips (the add-ons visibly linger for seconds). Instead we intercept the
 * parent's remove click and delete the whole group in ONE /cart/update.js call.
 * @param {Event} event
 */
function onRemoveClickCapture(event) {
  const target = event.target instanceof Element ? event.target : null;
  const button = target?.closest('.cart-items__remove');
  if (!button) return;
  const row = button.closest('tr');
  // Only a group PARENT cascades; removing a single add-on is left to the theme.
  if (!row || !row.classList.contains('bb-addon-parent')) return;
  const group = /** @type {HTMLElement} */ (row).dataset.bbAddonGroup;
  if (!group) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  removeGroup(group);
}

/** Remove every line sharing the group id in one request, with the theme's remove animation. */
async function removeGroup(group) {
  if (busy || reconciling) return;
  const rows = [...document.querySelectorAll(`tr[data-bb-addon-group="${group}"]`)];
  const keys = rows.map((r) => /** @type {HTMLElement} */ (r).dataset.key).filter(Boolean);
  if (!keys.length) return;

  reconciling = true; // also stops onCartUpdate from double-acting on the resulting event
  rows.forEach((r) => r.classList.add('removing'));
  try {
    const body = /** @type {Record<string, unknown>} */ ({
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    });
    const sections = cartSectionIds();
    if (sections) body.sections = sections;

    const response = await fetch(`${Theme.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const updated = await response.json();
    document.dispatchEvent(
      new CartUpdateEvent(updated, 'bb-addons', {
        source: 'bb-addons-reconcile',
        itemCount: updated.item_count,
        sections: updated.sections,
      })
    );
  } catch (error) {
    console.error('[bb-addons] group remove failed', error);
    rows.forEach((r) => r.classList.remove('removing'));
  } finally {
    reconciling = false;
  }
}

/**
 * Keep each add-on's quantity locked to its parent's (D5 — add-on qty = parent qty, 1:1).
 * When a tree/plant's quantity is edited on the cart page, its add-on lines must follow;
 * this returns the [key, quantity] pairs for any add-on whose quantity has drifted from
 * its parent's. The rule is parent-authoritative, so it also snaps a directly-edited add-on
 * back — the bundle can never desync. Orphans (being removed this pass) are skipped.
 * @param {{items: Array<{key: string, quantity: number, properties?: Record<string,string>}>}} cart
 * @param {string[]} orphanKeys
 * @returns {Array<[string, number]>}
 */
function findQuantityUpdates(cart, orphanKeys) {
  const orphaned = new Set(orphanKeys);
  const parentQty = new Map();
  for (const item of cart.items) {
    if (prop(item, '_addon_role') === 'parent') {
      const group = prop(item, '_addon_group');
      if (group) parentQty.set(group, item.quantity);
    }
  }
  const updates = [];
  for (const item of cart.items) {
    const role = prop(item, '_addon_role');
    const group = prop(item, '_addon_group');
    if (!role || role === 'parent' || !group || orphaned.has(item.key)) continue;
    const target = parentQty.get(group);
    if (typeof target === 'number' && target > 0 && item.quantity !== target) {
      updates.push([item.key, target]);
    }
  }
  return updates;
}

/** @param {{items: Array<{key: string, properties?: Record<string,string>}>}} cart */
function findOrphanKeys(cart) {
  const livingParents = new Set();
  for (const item of cart.items) {
    if (prop(item, '_addon_role') === 'parent') livingParents.add(prop(item, '_addon_group'));
  }
  const orphans = [];
  for (const item of cart.items) {
    const role = prop(item, '_addon_role');
    const group = prop(item, '_addon_group');
    if (role && role !== 'parent' && group && !livingParents.has(group)) orphans.push(item.key);
  }
  return orphans;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function prop(item, key) {
  return item.properties ? item.properties[key] : undefined;
}

function setText(scope, selector, value) {
  const el = scope.querySelector(selector);
  if (el) el.textContent = value;
}

/** Format an integer amount of cents as a localized currency string. */
function formatMoney(cents, currency) {
  const amount = (Number(cents) || 0) / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(amount);
  } catch (_) {
    return `$${amount.toFixed(2)}`;
  }
}

/**
 * Sum the selected add-ons × parent quantity and write it into the modal's total
 * row, so the customer sees the real add-on cost for a multi-quantity order.
 * @param {HTMLDialogElement} dialog
 */
function updateTotal(dialog) {
  const valueEl = dialog.querySelector('[data-bb-addons-total-value]');
  if (!valueEl) return;
  const quantity = (pending && pending.quantity) || 1;
  let cents = 0;
  for (const card of dialog.querySelectorAll('[data-bb-addon-card][aria-pressed="true"]')) {
    cents += (parseInt(/** @type {HTMLElement} */ (card).dataset.price || '0', 10) || 0) * quantity;
  }
  valueEl.textContent = formatMoney(cents, dialog.dataset.bbCurrency);
}

function cartSectionIds() {
  const ids = [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter(Boolean);
  return ids.length ? ids.join(',') : '';
}

async function fetchCart() {
  try {
    return await (await fetch(`${Theme.routes.cart_url}.js`)).json();
  } catch (error) {
    console.error('[bb-addons] could not read cart', error);
    return null;
  }
}

// ── Boot ─────────────────────────────────────────────────────────────────────

function boot() {
  if (typeof window === 'undefined') return;

  // Capture on window → runs before the theme's document-level capture delegation.
  window.addEventListener('click', onClickCapture, true);
  window.addEventListener('click', onRemoveClickCapture, true);
  window.addEventListener('submit', onSubmitCapture, true);
  document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);

  const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById(DIALOG_ID));
  if (dialog) {
    dialog.addEventListener('click', (event) => onDialogClick(dialog, event));
    // Escape / backdrop close still adds the plant (D1).
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('bb-modal-open'); // release scroll-lock [R-10]
      if (pending && !pending.finalized) finalize([]);
    });
  }
}

boot();
